// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import 'phoenix_html'

import { asyncViewHandler, onDocumentReady, Views } from '../viewHandler'
import { initCookieConsent } from './lib/cookie-consent'
import { initFollowButtons } from './lib/followButton'
import { initBlogLikeButtons } from './lib/blogLikeButtons'
import { screenshotRetrier } from './lib/screenshotRetrier'
import { BlogShowView } from './views/BlogShowView'
import { PagePremiumView } from './views/PagePremiumView'
import { RoutesIndexView } from './views/RoutesIndexView'
import { RoutesShowView } from './views/RoutesShowView'
import { trackFbEngagedVisitor } from './lib/fbEvents'
import { initMobileAppPromoBanner } from './lib/mobileAppPromoBanner'
import { initNotificationMenu } from './lib/notificationMenu'
import { trackPhoenixFlashEvent } from './lib/phoenixFlashEvents'
import { trackDataAttributeEvents, trackEngagedVisit } from './lib/eventTracker'

// Navbar shadow effect
function initNavbarScroll() {
  const navbar = document.getElementById('sticky_navbar')
  if (!navbar) return

  let ticking = false

  const handleScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        if (window.scrollY > 0) {
          navbar.classList.add('shadow')
        } else {
          navbar.classList.remove('shadow')
        }
        ticking = false
      })
      ticking = true
    }
  }

  window.addEventListener('scroll', handleScroll, { passive: true })
  // Initial check
  handleScroll()
}

// Initialize navbar scroll effect
initNavbarScroll()

// Toggles live inside the LiveView container, which live navigation destroys and
// rebuilds. Delegate from `document` so the handler survives every re-render.
function initToggles() {
  document.addEventListener('click', handleToggleClick)
}

function handleToggleClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null
  if (!target) {
    return
  }

  const trigger = target.closest<HTMLElement>('[data-toggle]')
  const toggledSelector = trigger?.dataset.toggle

  // A trigger with phx-click is owned by LiveView when the socket is live
  const isLiveViewOwned =
    trigger?.hasAttribute('phx-click') && typeof (window as any).liveSocket !== 'undefined'

  if (trigger && toggledSelector && !isLiveViewOwned) {
    event.preventDefault()
    document.querySelector<HTMLElement>(toggledSelector)?.classList.toggle('hidden')
  }

  closeDropdownsOnClickAway(target, toggledSelector)
}

function closeDropdownsOnClickAway(target: HTMLElement, toggledSelector?: string) {
  document.querySelectorAll<HTMLElement>('[data-toggle-click-away]').forEach((element) => {
    const selector = element.dataset.toggleClickAway

    if (!selector || selector === toggledSelector || element.contains(target)) {
      return
    }

    document.querySelector<HTMLElement>(selector)?.classList.add('hidden')
  })
}

// Only signed-in pages render the bell's dot. The socket code that keeps it
// fresh loads on demand, so the other pages (signed-out visitors, most of the
// traffic) don't download it.
function initLiveNotificationDots() {
  if (!document.querySelector('[data-notification-dot]')) {
    return
  }

  void import('./lib/liveNotificationDots').then((live) => live.followNotificationDots())
}

function initLogoutLinks() {
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null

    if (target?.closest('.ts-logout-link')) {
      localStorage.clear()
    }
  })
}

const views: Views = {
  BlogShowView,
  PagePremiumView,
  RoutesIndexView,
  RoutesShowView,
  RegionShowView: RoutesIndexView,
}

// Initialize all features
onDocumentReady(() => {
  initCookieConsent()
  initToggles()
  // After the toggles: it reads the panel's state once the toggle has run.
  initNotificationMenu()
  initLiveNotificationDots()
  initLogoutLinks()
  initFollowButtons()
  initBlogLikeButtons()
  initMobileAppPromoBanner()

  asyncViewHandler(views)

  document.querySelectorAll('[data-smooth-scroll]').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault()
      const href = (e.currentTarget as HTMLAnchorElement).getAttribute('href')
      if (!href) return

      const target = document.querySelector(href)
      if (!target) return

      // Hide target element if specified
      const hideTargetSelector = (e.currentTarget as HTMLElement).getAttribute('data-hide-target')
      if (hideTargetSelector) {
        const elementToHide = document.querySelector<HTMLElement>(hideTargetSelector)
        if (elementToHide && !elementToHide.classList.contains('hidden')) {
          elementToHide.classList.add('hidden')
        }

        if (elementToHide && elementToHide.style.display !== 'none') {
          elementToHide.style.display = 'none'
        }
      }

      target.scrollIntoView({
        behavior: getScrollBehavior(),
        block: 'start',
      })
    })
  })

  handleTableOfContentClick()
  screenshotRetrier()

  trackFbEngagedVisitor()
  trackEngagedVisit()
  trackPhoenixFlashEvent()
  trackDataAttributeEvents()
})

// Smooth, unless the reader asked for reduced motion. A copy of
// react/shared/utils/scrollBehavior.ts: importing that module, which the React
// chunks share, makes esbuild split it into a chunk every page would load.
function getScrollBehavior(): ScrollBehavior {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
}

function handleTableOfContentClick() {
  document.querySelectorAll('.table_of_content a').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault()
      const href = (e.currentTarget as HTMLAnchorElement).getAttribute('href')
      if (!href) return

      const target = document.querySelector(href)
      if (!target) return

      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
  })
}

function copyToClipboardWithFeedback(text: string, button: HTMLButtonElement) {
  navigator.clipboard.writeText(text)
  const originalText = button.textContent
  button.textContent = 'Copied!'
  button.classList.add('text-green-800')
  setTimeout(() => {
    button.textContent = originalText
    button.classList.remove('text-green-800')
  }, 2000)
}

// Make it globally available
;(window as any).copyToClipboardWithFeedback = copyToClipboardWithFeedback
